/**
 * The frame compositor: turns one recorded video frame + camera pose + styling
 * into the final polished output frame.
 *
 * Layer order:
 *   1. wallpaper
 *   2. window card shadow
 *   3. card body + clipped content (video with spring camera transform)
 *   4. browser chrome strip, click ripples, telemetry cursor
 *   5. card border + watermark
 *
 * Everything is drawn with plain Canvas 2D so it renders identically in the
 * live preview canvas and inside the MediaRecorder export canvas.
 */
import { clamp } from '../lib/format'
import {
  clampZoomCenter,
  contentRect,
  coverFit,
  type CameraPose,
  type Point,
  type Rect,
  videoNormToCanvasPoint,
} from '../lib/framing'
import type { TelemetrySample } from '../lib/telemetry'
import type { Wallpaper } from '../lib/wallpapers'
import { drawHaloText, paintWallpaper, rgba, roundRectPath } from './painters'

export interface WindowStyle {
  /** Wallpaper matte thickness, 0–120px. */
  padding: number
  /** Card corner radius, 0–32px. */
  cornerRadius: number
  /** Drop shadow intensity, 0–100. */
  shadow: number
  /** Fake browser chrome strip (traffic lights + address bar). */
  showChrome: boolean
}

export const DEFAULT_WINDOW_STYLE: WindowStyle = {
  padding: 48,
  cornerRadius: 16,
  shadow: 45,
  showChrome: true,
}

export interface Ripple {
  /** Click timestamp in seconds. */
  t: number
  /** Normalized position inside the recorded frame. */
  x: number
  y: number
}

export type WatermarkPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'

export interface WatermarkOptions {
  enabled: boolean
  text: string
  opacity: number
  position: WatermarkPosition
}

export const DEFAULT_WATERMARK: WatermarkOptions = {
  enabled: false,
  text: 'demo.polish',
  opacity: 0.55,
  position: 'bottom-right',
}

export const RIPPLE_DURATION = 0.6

export interface FrameRequest {
  ctx: CanvasRenderingContext2D
  width: number
  height: number
  /** Video frame (or sandbox canvas) — `null` before a source is loaded. */
  source: CanvasImageSource | null
  sourceWidth: number
  sourceHeight: number
  /** Playhead position in seconds. */
  time: number
  camera: CameraPose
  style: WindowStyle
  background: Wallpaper
  ripples?: readonly Ripple[]
  watermark?: WatermarkOptions | null
  cursor?: { sample: TelemetrySample | null; visible: boolean } | null
  /** `final` enables the extra (slower) ambient shadow passes. */
  quality?: 'draft' | 'final'
  /** Shown when there is no source yet. */
  emptyLabel?: string
}

export function drawFrame(request: FrameRequest): void {
  const { ctx, width, height, style } = request
  if (!ctx || width <= 0 || height <= 0) return

  ctx.save()
  ctx.clearRect(0, 0, width, height)
  paintWallpaper(ctx, request.background, width, height)

  const rect = contentRect(width, height, style.padding)
  const radius = Math.min(style.cornerRadius, Math.min(rect.width, rect.height) / 2)

  paintCardShadow(ctx, rect, radius, style.shadow, request.quality === 'final')

  // Card body + clipped content.
  ctx.save()
  roundRectPath(ctx, rect, radius)
  ctx.fillStyle = '#0b0d13'
  ctx.fill()
  ctx.clip()
  ctx.translate(rect.x, rect.y)

  drawVideoLayer(ctx, rect, request)
  if (style.showChrome) drawChrome(ctx, rect)
  drawRipples(ctx, rect, request)
  drawCursor(ctx, rect, request)
  ctx.restore()

  drawCardBorder(ctx, rect, radius)
  if (request.watermark?.enabled && request.watermark.text.trim().length > 0) {
    drawWatermark(ctx, rect, request.watermark)
  }
  ctx.restore()
}

/** Card body shadow (drawn as a black rounded rect, then covered by the body). */
function paintCardShadow(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  radius: number,
  intensity: number,
  final: boolean,
): void {
  if (intensity <= 0) return
  const passes: Array<{ blur: number; offset: number; alpha: number }> = [
    { blur: intensity * 1.25, offset: intensity * 0.22, alpha: 0.5 },
  ]
  if (final) {
    passes.push({ blur: intensity * 2.6, offset: intensity * 0.5, alpha: 0.28 })
    passes.push({ blur: intensity * 0.55, offset: intensity * 0.08, alpha: 0.4 })
  }
  ctx.save()
  for (const pass of passes) {
    ctx.shadowColor = `rgba(3,5,10,${pass.alpha})`
    ctx.shadowBlur = pass.blur
    ctx.shadowOffsetY = pass.offset
    roundRectPath(ctx, rect, radius)
    ctx.fillStyle = '#04060b'
    ctx.fill()
  }
  ctx.restore()
}

function drawVideoLayer(ctx: CanvasRenderingContext2D, rect: Rect, request: FrameRequest): void {
  const { source, sourceWidth, sourceHeight, camera } = request
  const base = coverFit(sourceWidth, sourceHeight, rect.width, rect.height)
  const zoom = Math.max(1e-6, camera.scale)
  const center = clampZoomCenter(camera.x, camera.y, zoom)

  ctx.save()
  const pivotX = center.x * rect.width
  const pivotY = center.y * rect.height
  ctx.translate(pivotX, pivotY)
  ctx.scale(zoom, zoom)
  ctx.translate(-pivotX, -pivotY)

  if (source) {
    try {
      ctx.drawImage(source, base.x, base.y, base.width, base.height)
    } catch {
      /* a not-yet-decoded frame is simply skipped */
    }
  } else {
    drawEmptySlot(ctx, request, base, rect)
  }
  ctx.restore()
}

function drawEmptySlot(ctx: CanvasRenderingContext2D, request: FrameRequest, base: Rect, rect: Rect): void {
  const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height)
  gradient.addColorStop(0, '#12161f')
  gradient.addColorStop(1, '#0a0c12')
  ctx.fillStyle = gradient
  ctx.fillRect(base.x, base.y, base.width, base.height)

  ctx.strokeStyle = 'rgba(255,255,255,0.06)'
  ctx.lineWidth = 1
  const step = Math.max(28, rect.width / 26)
  for (let x = base.x; x < base.x + base.width; x += step) {
    ctx.beginPath()
    ctx.moveTo(x, base.y)
    ctx.lineTo(x, base.y + base.height)
    ctx.stroke()
  }
  for (let y = base.y; y < base.y + base.height; y += step) {
    ctx.beginPath()
    ctx.moveTo(base.x, y)
    ctx.lineTo(base.x + base.width, y)
    ctx.stroke()
  }

  const label = request.emptyLabel ?? 'Record your screen or drop a video to start'
  const fontSize = Math.max(16, Math.min(34, rect.width * 0.028))
  ctx.font = `600 ${fontSize}px "Inter", system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = 'rgba(228,231,238,0.92)'
  ctx.fillText(label, rect.width / 2, rect.height / 2)
}

function drawChrome(ctx: CanvasRenderingContext2D, rect: Rect): void {
  const height = clamp(rect.height * 0.052, 22, 64)
  ctx.save()
  ctx.fillStyle = 'rgba(10,12,18,0.92)'
  ctx.fillRect(0, 0, rect.width, height)

  const dotRadius = Math.max(2.5, height * 0.13)
  const spacing = dotRadius * 3
  const cy = height / 2
  const dots = ['#f87171', '#fbbf24', '#34d399']
  dots.forEach((color, index) => {
    ctx.beginPath()
    ctx.fillStyle = color
    ctx.arc(height * 0.4 + index * spacing, cy, dotRadius, 0, Math.PI * 2)
    ctx.fill()
  })

  // Address-bar pill.
  const pillWidth = Math.min(rect.width * 0.3, height * 6)
  const pillHeight = height * 0.52
  const pillX = rect.width / 2 - pillWidth / 2
  const pillY = cy - pillHeight / 2
  ctx.fillStyle = 'rgba(255,255,255,0.07)'
  ctx.strokeStyle = 'rgba(255,255,255,0.08)'
  ctx.lineWidth = 1
  ctx.beginPath()
  const r = pillHeight / 2
  ctx.moveTo(pillX + r, pillY)
  ctx.arcTo(pillX + pillWidth, pillY, pillX + pillWidth, pillY + pillHeight, r)
  ctx.arcTo(pillX + pillWidth, pillY + pillHeight, pillX, pillY + pillHeight, r)
  ctx.arcTo(pillX, pillY + pillHeight, pillX, pillY, r)
  ctx.arcTo(pillX, pillY, pillX + pillWidth, pillY, r)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  ctx.fillStyle = 'rgba(228,231,238,0.45)'
  ctx.font = `500 ${Math.max(10, height * 0.32)}px "Inter", system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('demo.polish', rect.width / 2, cy + 0.5)

  // Hairline separating chrome from content.
  ctx.fillStyle = 'rgba(255,255,255,0.08)'
  ctx.fillRect(0, height, rect.width, 1)
  ctx.restore()
}

function drawRipples(ctx: CanvasRenderingContext2D, rect: Rect, request: FrameRequest): void {
  const ripples = request.ripples
  if (!ripples || ripples.length === 0) return
  for (const ripple of ripples) {
    const progress = (request.time - ripple.t) / RIPPLE_DURATION
    if (progress < 0 || progress > 1) continue
    const point = videoNormToCanvasPoint({ x: ripple.x, y: ripple.y }, {
      canvasWidth: request.width,
      canvasHeight: request.height,
      padding: request.style.padding,
      camera: request.camera,
      sourceWidth: request.sourceWidth,
      sourceHeight: request.sourceHeight,
    })
    const eased = 1 - (1 - progress) ** 3
    const radius = rect.width * (0.02 + 0.075 * eased)
    const alpha = (1 - progress) * 0.75
    ctx.save()
    ctx.strokeStyle = `rgba(255,255,255,${alpha.toFixed(3)})`
    ctx.lineWidth = Math.max(1.5, rect.width * 0.003)
    ctx.beginPath()
    ctx.arc(point.x, point.y, radius, 0, Math.PI * 2)
    ctx.stroke()
    if (progress < 0.35) {
      ctx.fillStyle = `rgba(255,255,255,${(0.7 * (1 - progress / 0.35)).toFixed(3)})`
      ctx.beginPath()
      ctx.arc(point.x, point.y, Math.max(2, rect.width * 0.006), 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }
}

function drawCursor(ctx: CanvasRenderingContext2D, rect: Rect, request: FrameRequest): void {
  const cursor = request.cursor
  if (!cursor || !cursor.visible || !cursor.sample) return
  const point = videoNormToCanvasPoint(cursor.sample, {
    canvasWidth: request.width,
    canvasHeight: request.height,
    padding: request.style.padding,
    camera: request.camera,
    sourceWidth: request.sourceWidth,
    sourceHeight: request.sourceHeight,
  })
  const size = Math.max(10, rect.width * 0.022)
  ctx.save()
  ctx.translate(point.x, point.y)
  ctx.shadowColor = 'rgba(0,0,0,0.6)'
  ctx.shadowBlur = size * 0.5
  ctx.fillStyle = 'rgba(255,255,255,0.95)'
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(0, size * 1.5)
  ctx.lineTo(size * 0.38, size * 1.12)
  ctx.lineTo(size * 0.62, size * 1.62)
  ctx.lineTo(size * 0.9, size * 1.42)
  ctx.lineTo(size * 0.66, size * 0.94)
  ctx.lineTo(size * 1.1, size * 0.88)
  ctx.closePath()
  ctx.fill()
  ctx.strokeStyle = 'rgba(0,0,0,0.35)'
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()
}

function drawCardBorder(ctx: CanvasRenderingContext2D, rect: Rect, radius: number): void {
  ctx.save()
  roundRectPath(ctx, rect, radius)
  ctx.strokeStyle = 'rgba(255,255,255,0.14)'
  ctx.lineWidth = Math.max(1, rect.width * 0.0015)
  ctx.stroke()
  ctx.restore()
}

function drawWatermark(ctx: CanvasRenderingContext2D, rect: Rect, watermark: WatermarkOptions): void {
  const fontSize = Math.max(14, rect.width * 0.02)
  const margin = Math.max(14, rect.width * 0.022)
  const font = `600 ${fontSize}px "Inter", system-ui, sans-serif`
  const align: CanvasTextAlign = watermark.position.endsWith('right') ? 'right' : 'left'
  const x = watermark.position.endsWith('right') ? rect.x + rect.width - margin : rect.x + margin
  const y = watermark.position.startsWith('bottom') ? rect.y + rect.height - margin : rect.y + margin + fontSize
  drawHaloText(ctx, watermark.text, x, y, {
    font,
    color: `rgba(255,255,255,${clamp(watermark.opacity, 0, 1).toFixed(3)})`,
    align,
    halo: 'rgba(0,0,0,0.55)',
    haloBlur: fontSize * 0.7,
  })
}

/** Convert a canvas-relative click into a ripple/keyframe payload. */
export function rippleAt(point: Point, time: number): Ripple {
  return { t: time, x: clamp(point.x, 0, 1), y: clamp(point.y, 0, 1) }
}
